import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Clock,
  CalendarDays,
  Timer,
  Hourglass,
  Banknote,
  HandCoins,
  PauseCircle,
  Calculator,
  LayoutList,
  MinusCircle,
  FileBarChart,
  History,
  Settings,
  Menu,
  Sun,
  Moon,
  LogOut,
  ShieldCheck,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/Button.jsx';
import { Breadcrumbs } from '@/components/Breadcrumbs.jsx';
import { Select } from '@/components/ui/Select.jsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu.jsx';
import { useAuthStore } from '@/stores/auth.js';
import { useThemeStore } from '@/stores/theme.js';
import { usePeriodStore } from '@/stores/period.js';
import { usePerm } from '@/hooks/usePerm.js';
import { cn } from '@/lib/cn.js';
import { monthName } from '@/lib/format.js';

const NAV = [
  { group: 'Overview', items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }] },
  {
    group: 'HR',
    items: [
      { to: '/employees', label: 'Employees', icon: Users, perm: 'employees.read' },
      { to: '/attendance', label: 'Attendance', icon: Clock, perm: 'attendance.read' },
      { to: '/leave', label: 'Leave', icon: CalendarDays, perm: 'leave.read' },
      { to: '/overtime', label: 'Overtime', icon: Timer, perm: 'overtime.read' },
      { to: '/short-leave', label: 'Short Leave', icon: Hourglass, perm: 'short-leave.read' },
    ],
  },
  {
    group: 'Payroll',
    items: [
      { to: '/loans', label: 'Loans', icon: Banknote, perm: 'loans.read' },
      { to: '/advances', label: 'Advances', icon: HandCoins, perm: 'advances.read' },
      { to: '/salary-holds', label: 'Salary Holds', icon: PauseCircle, perm: 'salary-holds.read' },
      { to: '/payroll/allowances', label: 'Allowances', icon: LayoutList, perm: 'payroll.allowance.write' },
      { to: '/payroll/deductions', label: 'Deductions', icon: MinusCircle, perm: 'payroll.deduction.write' },
      { to: '/payroll', label: 'Payroll Runs', icon: Calculator, end: true, perm: 'payroll.run.read' },
    ],
  },
  {
    group: 'Insights',
    items: [
      { to: '/reports', label: 'Reports', icon: FileBarChart, perm: 'reports.view' },
      { to: '/audit', label: 'Audit Log', icon: History, perm: 'audit.view' },
    ],
  },
  {
    group: 'Settings',
    items: [
      { to: '/settings/master', label: 'Master Details', icon: Settings, perm: 'settings.master.read' },
      { to: '/settings/hr-policies', label: 'HR Policies', icon: ShieldCheck, perm: 'settings.policies.write' },
      { to: '/settings/preference', label: 'Preference', icon: SlidersHorizontal, perm: 'settings.users.manage' },
    ],
  },
];

export default function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [openSettings, setOpenSettings] = useState(true);
  const { user, logout } = useAuthStore();
  const { theme, toggle } = useThemeStore();
  const { year, month, setPeriod } = usePeriodStore();
  const { hasPerm } = usePerm();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-full bg-slate-50 dark:bg-slate-950">
      <aside
        className={cn(
          'flex flex-col border-r border-brand-800/60 bg-brand-900 transition-all',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        <div className="flex h-14 items-center gap-2 px-4 border-b border-brand-800/60">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-600 text-white font-bold">VC</div>
          {!collapsed && <span className="font-semibold text-white">Lanka Payroll</span>}
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-3">
           {NAV.map((group) => {
             const isSettings = group.group === 'Settings';
             const isOpen = !isSettings || openSettings;
             // Admin users have access to everything; others filtered by permissions
             const visibleItems = group.items.filter(item => {
               if (!item.perm) return true; // No permission required
               if (user?.role === 'admin') return true; // Admin has all access
               return hasPerm(item.perm); // Check specific permission for others
             });
             
             // Skip group if no visible items
             if (visibleItems.length === 0) return null;
             
             return (
               <div key={group.group}>
                 {!collapsed && (
                   <button
                     type="button"
                     onClick={() => isSettings && setOpenSettings((v) => !v)}
                     className="w-full flex items-center justify-between px-2 mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40"
                   >
                     {group.group}
                     {isSettings && (isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                   </button>
                 )}
                 {isOpen && (
                   <div className="space-y-0.5">
                     {visibleItems.map((item) => (
                       <NavLink
                         key={item.to}
                         to={item.to}
                         end={item.end}
                         title={collapsed ? item.label : undefined}
                         className={({ isActive }) =>
                           cn(
                             'flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors',
                             collapsed && 'justify-center',
                             isActive
                               ? 'bg-white/15 text-white'
                               : 'text-white/70 hover:bg-white/10 hover:text-white',
                           )
                         }
                       >
                         <item.icon className="h-4 w-4 shrink-0" />
                         {!collapsed && <span className="truncate">{item.label}</span>}
                       </NavLink>
                     ))}
                   </div>
                 )}
               </div>
             );
           })}
         </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex h-14 items-center gap-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4">
          <Button variant="ghost" size="icon" onClick={() => setCollapsed((v) => !v)}>
            <Menu className="h-4 w-4" />
          </Button>
          <Breadcrumbs />
          <div className="flex-1" />

          <div className="hidden md:flex items-center gap-2">
            <span className="text-xs text-slate-500">Period:</span>
            <Select value={month} onChange={(e) => setPeriod(year, Number(e.target.value))} className="h-8 w-32">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {monthName(m)}
                </option>
              ))}
            </Select>
            <Select value={year} onChange={(e) => setPeriod(Number(e.target.value), month)} className="h-8 w-24">
              {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 3 + i).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </div>

          <Button variant="ghost" size="icon" onClick={toggle} title="Toggle theme">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2">
                <div className="h-7 w-7 rounded-full bg-brand-600 text-white text-xs grid place-items-center">
                  {(user?.name || 'U').slice(0, 1).toUpperCase()}
                </div>
                <span className="hidden sm:inline text-sm">{user?.name || 'User'}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user?.name}</div>
                <div className="text-xs text-slate-500">{user?.email}</div>
                <div className="text-xs mt-1 text-brand-600 uppercase">{user?.role}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/settings/preference">Account & Users</Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onLogout}>
                <LogOut className="h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
